import { patchState, signalStore, type, withComputed, withMethods } from '@ngrx/signals';
import { entityConfig, removeEntity, setEntity, withEntities } from '@ngrx/signals/entities';
import { IrisDmsSettingsAlias, IrisDmsUserSettingsResponseI } from '../models/IrisDmsSettings';
import { computed, inject } from '@angular/core';
import { IrisUserSettingsService } from '@iris/common/services/user-settings.service';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { map, mergeMap, pipe, switchMap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { IrisUserService } from '@iris/common/services/user.service';
import { IrisQueryParamsOrderBy } from '@iris/api-query';
import { ColDef } from 'ag-grid-community';

const config = entityConfig({
  entity: type<IrisDmsUserSettingsResponseI>(),
  selectId: entity => entity.module,
});

export const IrisDmsSettingsStore = signalStore(
  { providedIn: 'root' },
  withEntities(config),
  withComputed((
    store,
    userService = inject(IrisUserService),
  ) => ({
    userId: computed(() => userService?.me?.id),
    folderSettings: computed(() => store.entityMap()[IrisDmsSettingsAlias.Folder]),
    trashBinSettings: computed(() => store.entityMap()[IrisDmsSettingsAlias.Trash]),
    filterSettings: computed(() => store.entityMap()[IrisDmsSettingsAlias.Filter]),
    recentFilesSettings: computed(() => store.entityMap()[IrisDmsSettingsAlias.RecentFiles]),
    systemFolderSettings: computed(() => store.entityMap()[IrisDmsSettingsAlias.SystemFolder]),
    systemTrashBinSettings: computed(() => store.entityMap()[IrisDmsSettingsAlias.SystemTrash]),
  })),
  withMethods((
    store,
  ) => ({
    getUserSettings: (alias: IrisDmsSettingsAlias) => computed(() => store.entityMap()[alias]),
  })),
  withMethods((
    store,
    userSettingsService = inject(IrisUserSettingsService),
  ) => ({
    fetchDmsSettings: rxMethod<IrisDmsSettingsAlias>(
      pipe(
        mergeMap(alias => userSettingsService.getUserSettingsById<IrisDmsUserSettingsResponseI>(alias, store.userId()).pipe(
          tapResponse({
            next: settings => patchState(store, setEntity(settings, config)),
            error: () => patchState(store, removeEntity(alias)),
          }),
        )),
      ),
    ),
    updateDmsSettings: rxMethod<{ alias: IrisDmsSettingsAlias; settings: IrisDmsUserSettingsResponseI }>(
      pipe(
        switchMap(({ alias, settings }) => userSettingsService.saveUserSettings<IrisDmsUserSettingsResponseI>(alias, store.userId(), settings).pipe(
          map(updatedSettings => patchState(store, setEntity(updatedSettings, config))),
        )),
      ),
    ),
    updateUserColumnDefs: rxMethod<{ alias: IrisDmsSettingsAlias; columnDefs: ColDef[] }>(
      pipe(
        switchMap(({ alias, columnDefs }) => {
          const userSettings = store.getUserSettings(alias)();
          const columns = columnDefs.map(({ field, hide }, index) => ({ field, hide, index }));
          const newUserSettings = { ...userSettings, settings: { ...userSettings?.settings, columns } };
          return userSettingsService.saveUserSettings<IrisDmsUserSettingsResponseI>(alias, store.userId(), newUserSettings)
            .pipe(
              map(updatedSettings => patchState(store, setEntity(updatedSettings, config))),
            );
        }),
      ),
    ),
    updateUserOrderBy: rxMethod<{ alias: IrisDmsSettingsAlias; orderBy: IrisQueryParamsOrderBy[] }>(
      pipe(
        switchMap(({ alias, orderBy }) => {
          const userSettings = store.getUserSettings(alias)();
          const newUserSettings = { ...userSettings, settings: { ...userSettings.settings, orderBy } };

          return userSettingsService.saveUserSettings<IrisDmsUserSettingsResponseI>(alias, store.userId(), newUserSettings)
            .pipe(
              map(updatedSettings => patchState(store, setEntity(updatedSettings, config))),
            );
        }),
      ),
    ),
  })),
);
